import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import Button from '@/components/global/Button';
import { Column, Grid } from '@/components/global/Grid';
import NoResults from '@/components/global/NoResults';
import { Pagination } from '@/components/global/Pagination';
import { useRegions } from '@/components/providers/RegionsProvider';
import checkoutDataLayer from '@/lib/datalayer/checkout';
import { useTypefaceCollectionLazyQuery } from '@/saleor/api';

import { messages } from '../translations';
import TypefaceCard from '../typeface/TypefaceCard/TypefaceCard';
import { TYPEFACES_FILTERING_CATEGORIES } from './helpers/typefaceCollectionHelpers';
import styles from './TypefaceCollection.module.scss';
import TypefaceHeader from './TypefaceHeader/TypefaceHeader';

export interface ITypefaceCollectionProps {
  categoryId?: string;
  allowMore?: boolean;
  serverProductsData?: any;
  childCategories?: any;
  attributes?: any;
}

export const TypefaceCollection = ({
  categoryId,
  allowMore = true,
  serverProductsData,
  childCategories,
  attributes
}: ITypefaceCollectionProps) => {
  const t = useIntl();
  const { query } = useRegions();
  const [customText, setCustomText] = useState('');
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [compareProducts, setCompareProducts] = useState([]);
  const [productData, setProductData] = useState<any>();
  const [viewComparedProducts, setViewComparedProducts] = useState(false);
  const searchState = useState('');
  const [search] = searchState;

  const [filters, setFilters] = useState({ categories: [categoryId], attributes: null });
  const [sort, setSort] = useState<any>(TYPEFACES_FILTERING_CATEGORIES[0].value);

  const productRenderData = useCallback(() => {
    return productData ? productData : serverProductsData;
  }, [serverProductsData, productData]);

  const [getProducts, { loading, fetchMore, data }] = useTypefaceCollectionLazyQuery({
    fetchPolicy: 'cache-and-network',
    onCompleted: (result) => {
      const newProductData = {
        pageInfo: result?.products?.pageInfo,
        totalCount: result?.products?.totalCount,
        edges: [...productRenderData().edges, ...result?.products.edges]
      };

      setProductData(paginationLoading ? newProductData : result?.products);
    }
  });

  useEffect(() => {
    if (compareProducts.length < 2) {
      setViewComparedProducts(false);
    }
  }, [compareProducts]);

  useMemo(() => {
    getProducts({
      variables: {
        sort: sort,
        filter: filters,
        after: null,
        first: 9,
        ...query
      }
    });
  }, [filters, sort]);

  const onLoadMore = async (cursor) => {
    if (loading) {
      return;
    }

    setPaginationLoading(true);
    await getProducts({
      variables: {
        sort: sort,
        filter: filters,
        first: 10,
        after: cursor,
        ...query
      }
    });
    setPaginationLoading(false);
  };

  const products = useMemo(
    () => productRenderData()?.edges.map((edge) => edge.node) || [],
    [productRenderData]
  );

  const toggleCompareProduct = (product) => {
    const productInState = compareProducts.some((item) => item === product);
    const newCompareArray = productInState
      ? [...compareProducts.filter((item) => item !== product)]
      : [...compareProducts, product];
    return setCompareProducts(newCompareArray);
  };

  const itemIsChecked = (product) => compareProducts.some((item) => item === product);

  const renderProducts = viewComparedProducts
    ? products.filter((product) => compareProducts.includes(product.id))
    : products;

  const productCount = viewComparedProducts
    ? renderProducts.length
    : productRenderData()?.totalCount;

  const listingsElementRef = useCallback(
    (node) => {
      if (node !== null && node.firstChild !== null && node.children.length > 1) {
        const firstElementHeight = node.firstChild.getBoundingClientRect().height || 300;
        const defaultElementHeight =
          node.children.length > 0 ? node.children[1].getBoundingClientRect().height : 225;
        const productCountUpToEven = 2 * Math.round((productCount - 1) / 2);
        const rowGap = (productCountUpToEven / 2) * 24;

        document.documentElement.style.setProperty('--first-el-height', `${firstElementHeight}px`);
        document.documentElement.style.setProperty(
          '--default-el-height',
          `${defaultElementHeight}px`
        );
        document.documentElement.style.setProperty(
          '--typefacePage-height-mobile',
          `${defaultElementHeight * productCount + firstElementHeight + rowGap}px`
        );
        document.documentElement.style.setProperty(
          '--typefacePage-height',
          `${defaultElementHeight * (productCountUpToEven / 2) + firstElementHeight + rowGap}px`
        );
      }
    },
    [productCount]
  );

  const containerRef = useRef<HTMLDivElement>();

  const TypefacesShell = () => {
    return (
      <div className={styles.TypefaceShell}>
        {[...Array(productCount)].map((e, i) => (
          <div className={styles.TypefaceShell__Item} key={i}>
            <span></span>
            <div />
          </div>
        ))}
      </div>
    );
  };

  // Scroll to top on first render
  useEffect(() => {
    if (containerRef?.current) containerRef?.current?.scrollTo(0, 0);
    // Remove the scroll restoration
    history.scrollRestoration = 'manual';
  }, [containerRef]);

  // add 'view_item_list' to the dataLayer
  useEffect(() => {
    checkoutDataLayer.viewItemList(products, 'typefaces');
  }, [products]);

  return (
    <div ref={containerRef}>
      <Grid>
        <Column sm={12} md={12} lg={12}>
          <TypefaceHeader
            childCategories={childCategories}
            attributes={attributes}
            categoryId={categoryId}
            searchState={searchState}
            isComparing={viewComparedProducts}
            filters={filters}
            setFilters={setFilters}
            sort={sort}
            setSort={setSort}
          />
          {search.length > 0 && products.length === 0 && (
            <NoResults
              title="No Results for your search"
              text={t.formatMessage(messages.noProducts)}
            />
          )}
          {search.length === 0 && products.length === 0 && (
            <NoResults title="No Results" text={t.formatMessage(messages.noProductsFiltered)} />
          )}

          {products.length > 0 && (
            <div className={styles.TypefaceCollectionWrapper}>
              {!viewComparedProducts && !search && <TypefacesShell />}
              <ul
                role="list"
                ref={listingsElementRef}
                className={clsx(styles.TypefaceCollection, {
                  [styles.TypefaceCollectionFullWidth]: viewComparedProducts
                })}
              >
                {renderProducts.map((product, i) => (
                  <li
                    className={clsx(
                      styles.productLi,
                      i === 0 && !viewComparedProducts && styles.productLiFirst
                    )}
                    key={product.id}
                  >
                    <TypefaceCard
                      toggleCompareProduct={toggleCompareProduct}
                      key={product.id}
                      product={product}
                      customText={customText}
                      checked={itemIsChecked(product.id)}
                    />
                  </li>
                ))}
              </ul>
              {allowMore && !viewComparedProducts && (
                <Pagination
                  onLoadMore={onLoadMore}
                  pageInfo={data?.products?.pageInfo}
                  loadOnScroll
                />
              )}
            </div>
          )}
          <div className={styles.floatingCustomText}>
            <div>
              <input
                maxLength={50}
                type="text"
                placeholder="Your text..."
                onChange={(e) => {
                  setCustomText(e.target.value);
                }}
              />
              <Button
                callBack={() => {
                  setViewComparedProducts(!viewComparedProducts);
                }}
                variant="primary"
                disabled={compareProducts.length < 2}
                muted={compareProducts.length < 2}
              >
                {!viewComparedProducts ? 'Compare' : 'Clear'}
              </Button>
            </div>
          </div>
        </Column>
      </Grid>
    </div>
  );
};

export default TypefaceCollection;
